/**
 * Build timeline sub-items for an answers-snapshot property
 * (`ahoy_custom_answers` / `ahoy_booking_answers`).
 *
 * The snapshot is decomposed into one sub-item per question — `[type icon]
 * Label: value` — so the answers sit beside the event's other fields as peers
 * rather than as one nested block. This is the answers-snapshot twin of
 * `buildJsonSubItems`: the rows are self-describing (`label` + `type`), so no
 * companion schema is needed.
 *
 * With a `previousValue` (a property-updated event) only the questions whose
 * answer changed — or that are new, or were removed — are listed, each as an
 * `old → new` diff through `PropertyDiffValue`, the same renderer every other
 * changed property gets.
 */

import type { ReactElement } from 'react';

import { PropertyValueLabel } from '@/lib/crm/components/properties/labels/PropertyValueLabel';
import type { BookingAnswer } from '@/lib/crm/utils/bookingQuestions';
import {
  buildBookingAnswerPropertyType,
  formatAnswer,
  getBookingAnswerDisplayValue,
  parseBookingAnswers,
} from '@/lib/crm/utils/bookingQuestions';
import { getPropertyTypeIconComponent } from '@/lib/crm/utils/icons/PropertyTypeIcon';
import { unwrapPropertyValue } from '@/types/crm';

import { PropertyDiffValue } from '../components/shared/PropertyDiffValue';
import type { SubItemContent } from '../types/renderer';

// Matches the icon treatment of every other property sub-item
// (ObjectCreatedItem / PropertyUpdatedItem `getPropertyIcon`).
const ICON_CLASS = 'w-4 h-4 text-primary';

// Same visible-text budget the sibling property sub-items use.
const MAX_INLINE_LENGTH = 150;

interface BuildBookingAnswerSubItemsOptions {
  /** The event's new snapshot — native array, `{ value }` wrapper, or JSON string. */
  value: unknown;
  /** The prior snapshot from `old_property_values`; omit for creation events. */
  previousValue?: unknown;
  /** Org for clickable mentions inside text answers; the timeline items read it from the route. */
  orgId: number | undefined;
}

function answerText(answer: BookingAnswer | undefined): string {
  return answer ? formatAnswer(answer.answer, answer.type) || '—' : '—';
}

function isSameAnswer(
  a: BookingAnswer | undefined,
  b: BookingAnswer | undefined
): boolean {
  return (
    JSON.stringify(a?.answer ?? null) === JSON.stringify(b?.answer ?? null)
  );
}

function buildIcon(answer: BookingAnswer): ReactElement {
  const Icon = getPropertyTypeIconComponent(
    buildBookingAnswerPropertyType(answer)
  );
  return <Icon className={ICON_CLASS} />;
}

function buildCreatedSubItem(
  answer: BookingAnswer,
  orgId: number | undefined
): SubItemContent {
  const propertyType = buildBookingAnswerPropertyType(answer);
  return {
    icon: buildIcon(answer),
    text: (
      <span>
        {answer.label}:{' '}
        <PropertyValueLabel
          value={getBookingAnswerDisplayValue(answer)}
          propertyType={propertyType}
          orgId={orgId}
          fontSize='sm'
          className='font-medium'
          maxLength={MAX_INLINE_LENGTH}
        />
      </span>
    ),
    ariaLabel: `${answer.label}: ${answerText(answer)}`,
  };
}

function buildChangedSubItem(
  answer: BookingAnswer,
  previous: BookingAnswer,
  orgId: number | undefined
): SubItemContent {
  const propertyType = buildBookingAnswerPropertyType(answer);
  return {
    icon: buildIcon(answer),
    text: (
      <span>
        {answer.label}:{' '}
        <PropertyDiffValue
          oldValue={getBookingAnswerDisplayValue(previous)}
          newValue={getBookingAnswerDisplayValue(answer)}
          schema={propertyType}
          orgId={orgId}
        />
      </span>
    ),
    ariaLabel: `${answer.label}: ${answerText(previous)} → ${answerText(answer)}`,
  };
}

export function buildBookingAnswerSubItems({
  value,
  previousValue,
  orgId,
}: BuildBookingAnswerSubItemsOptions): SubItemContent[] {
  const answers = parseBookingAnswers(unwrapPropertyValue(value));
  const previousAnswers =
    previousValue === undefined
      ? []
      : parseBookingAnswers(unwrapPropertyValue(previousValue));

  // Creation event, or an update whose prior snapshot is empty: every answer
  // is new, so list them plainly.
  if (previousAnswers.length === 0) {
    return answers.map(answer => buildCreatedSubItem(answer, orgId));
  }

  const previousByKey = new Map(previousAnswers.map(a => [a.key, a]));
  const currentKeys = new Set(answers.map(a => a.key));

  const changed = answers.flatMap(answer => {
    const previous = previousByKey.get(answer.key);
    // A newly added question has no prior value: render it plainly (an `old
    // → new` diff against a missing prior reads as `— → new`), and surface it
    // even when its answer is empty so `Label: —` still appears.
    if (previous === undefined) {
      return [buildCreatedSubItem(answer, orgId)];
    }
    // An existing question whose answer changed reads as an `old → new` diff.
    return isSameAnswer(answer, previous)
      ? []
      : [buildChangedSubItem(answer, previous, orgId)];
  });

  // A question dropped from the snapshot still reads as a change: old → —.
  const removed = previousAnswers
    .filter(previous => !currentKeys.has(previous.key))
    .map(previous =>
      buildChangedSubItem({ ...previous, answer: null }, previous, orgId)
    );

  return [...changed, ...removed];
}
